import { coreSignatureOf, signatureOf, workflowOfRow } from './inverse'

// ----------------------------------------------------------------------
// Setups: the batch-level workflows the user started again and again lately, read
// from the recent analyses (/analysis/list rows, the list the batches already read).
//
// A start belongs to a batch when its body carries batch.id. The starts of one batch
// are read as one workflow: a sample left out of AI or CNV, or a PacBio file without
// methylation data, does not make it another setup (the switches are OR-ed).
// Untagged starts (one sample at a time, or the old wizard) stand alone; for the
// typical batch size, the same setup's untagged starts on one day count as a batch.
// ----------------------------------------------------------------------

export const SETUP_WINDOW_DAYS = 60
export const RECENT_DAYS = 30
// Kept when started this many times, or in this many batches.
export const MIN_USES = 3
export const MIN_BATCHES = 2
// A start counts half as much two weeks later.
export const HALF_LIFE_DAYS = 14

const DAY_MS = 24 * 60 * 60 * 1000
// Rows dated a little ahead (a clock that is off) still count.
const FUTURE_SLACK_MS = DAY_MS

const timeOf = (row) => {
  const t = new Date(row?.created_at).getTime()
  return Number.isFinite(t) ? t : NaN
}

const dayKey = (ms) => {
  const d = new Date(ms)
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

const batchOf = (row) => {
  const batch = row?.analysis_parameters?.batch
  if (!batch || typeof batch !== 'object' || batch.id === undefined || batch.id === null || batch.id === '') return null
  return { id: String(batch.id), name: typeof batch.name === 'string' ? batch.name.trim() : '' }
}

// The high median: always a size that occurred ("usually in batches of 12" for
// batches of 1, 1, 12 and 12, not an average of 7 that never ran).
export const median = (values) => {
  if (!values.length) return 0
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

// The workflow of a batch's starts: the first one's, with each switch on when it was
// on for any sample.
const mergeBatch = (workflows) => {
  const merged = { ...workflows[0] }
  if (merged.analysis === 'germline' && workflows.some((wf) => wf.interpretation === 'ai')) merged.interpretation = 'ai'
  const withCnv = workflows.find((wf) => wf.cnv)
  if (withCnv) {
    merged.cnv = true
    merged.cnvMethod = withCnv.cnvMethod
    merged.cnvCohortId = withCnv.cnvCohortId
  }
  ;['lrCnv', 'lrSv', 'lrMeth'].forEach((field) => {
    if (workflows.some((wf) => wf[field])) merged[field] = true
  })
  const named = workflows.find((wf) => wf.kitName)
  if (named) merged.kitName = named.kitName
  return merged
}

const sameTumour = (a, b) => (a?.oncotree_code || null) === (b?.oncotree_code || null)

/**
 * The setups of the last SETUP_WINDOW_DAYS days, best first:
 * [{ signature, workflow, count, recentCount, lastUsedAt, firstUsedAt, daysUsed,
 *    batchCount, typicalBatchSize, pastBatchNames: [{ name, at }], score }].
 * count: starts in the window; recentCount: starts in the last RECENT_DAYS days;
 * batchCount: tagged batches; typicalBatchSize: the median size of its batches
 * (tagged, or untagged starts on one day); pastBatchNames newest first.
 * Kept when started MIN_USES times or in MIN_BATCHES batches; ranked by starts
 * weighted by age (halved every HALF_LIFE_DAYS days).
 */
export function mineSetups(analyses, now = Date.now()) {
  const rows = Array.isArray(analyses) ? analyses : []
  const from = now - SETUP_WINDOW_DAYS * DAY_MS

  // 1. Each start in the window, with its workflow.
  const starts = []
  rows.forEach((row) => {
    const at = timeOf(row)
    if (!Number.isFinite(at) || at < from || at > now + FUTURE_SLACK_MS) return
    const workflow = workflowOfRow(row)
    if (!workflow) return
    starts.push({ at, workflow, batch: batchOf(row) })
  })

  // 2. A tagged batch reads as one workflow.
  const tagged = new Map()
  starts.forEach((start) => {
    if (!start.batch) return
    const key = `${start.batch.id}|${coreSignatureOf(start.workflow)}`
    if (!tagged.has(key)) tagged.set(key, [])
    tagged.get(key).push(start)
  })
  tagged.forEach((group) => {
    const merged = mergeBatch(group.map((start) => start.workflow))
    group.forEach((start) => {
      start.workflow = { ...merged, tumourType: start.workflow.tumourType }
    })
  })

  // 3. Setups by signature.
  const setups = new Map()
  starts.forEach((start) => {
    const signature = signatureOf(start.workflow)
    if (!signature) return
    if (!setups.has(signature)) setups.set(signature, { signature, starts: [] })
    setups.get(signature).starts.push(start)
  })

  const recentFrom = now - RECENT_DAYS * DAY_MS
  const result = []
  setups.forEach(({ signature, starts: list }) => {
    const sorted = [...list].sort((a, b) => b.at - a.at)
    const newest = sorted[0]

    // Batches: tagged ones by id, untagged starts by day.
    const groups = new Map()
    const names = new Map()
    sorted.forEach((start) => {
      const key = start.batch ? `b:${start.batch.id}` : `d:${dayKey(start.at)}`
      groups.set(key, (groups.get(key) || 0) + 1)
      if (start.batch && start.batch.name) {
        const lower = start.batch.name.toLowerCase()
        if (!names.has(lower)) names.set(lower, { name: start.batch.name, at: start.at })
      }
    })
    const batchCount = [...groups.keys()].filter((key) => key.startsWith('b:')).length
    const count = sorted.length
    if (count < MIN_USES && batchCount < MIN_BATCHES) return

    // The newest start's workflow; a display name for the kit from any start; the
    // default tumour type only when every start had the same one.
    const workflow = { ...newest.workflow }
    const named = sorted.find((start) => start.workflow.kitName)
    if (named) workflow.kitName = named.workflow.kitName
    if (!sorted.every((start) => sameTumour(start.workflow.tumourType, newest.workflow.tumourType))) {
      workflow.tumourType = null
    }

    result.push({
      signature,
      workflow,
      count,
      recentCount: sorted.filter((start) => start.at >= recentFrom).length,
      lastUsedAt: newest.at,
      firstUsedAt: sorted[sorted.length - 1].at,
      daysUsed: new Set(sorted.map((start) => dayKey(start.at))).size,
      batchCount,
      typicalBatchSize: median([...groups.values()]),
      pastBatchNames: [...names.values()].sort((a, b) => b.at - a.at),
      score: sorted.reduce((sum, start) => sum + 0.5 ** (Math.max(0, now - start.at) / DAY_MS / HALF_LIFE_DAYS), 0),
    })
  })

  return result.sort((a, b) => b.score - a.score || b.lastUsedAt - a.lastUsedAt || (a.signature < b.signature ? -1 : 1))
}
